import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase';
import { generateInvoiceGLEntries, deleteGLTransaction, getGLTransactions } from './glTransactionServices';

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
export type PaymentMethod = 'Bank Transfer' | 'Credit Card' | 'Cash' | 'Direct Debit' | 'Other';

export interface Invoice {
  id?: string;
  invoiceNumber: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  amount: number;
  status: InvoiceStatus;
  issueDate: Date | Timestamp;
  dueDate: Date | Timestamp;
  paymentDate?: Date | Timestamp;
  paymentMethod?: PaymentMethod;
  items: InvoiceItem[];
  notes?: string;
  terms?: string;
  createdBy?: string;
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  glAccountId?: string; // Reference to GL account for revenue
}

// Convert Firestore timestamp to Date
export const convertTimestampToDate = (timestamp: any): Date => {
  if (timestamp?.toDate) {
    return timestamp.toDate();
  }
  return new Date(timestamp || Date.now());
};

// Get invoices with optional date range filtering
export const getInvoices = async (
  startDate?: Date | null, 
  endDate?: Date | null
): Promise<Invoice[]> => {
  try {
    let invoicesQuery = query(
      collection(db, 'invoices'),
      orderBy('issueDate', 'desc')
    );

    // Apply date range filters if provided
    if (startDate) {
      invoicesQuery = query(
        invoicesQuery,
        where('issueDate', '>=', startDate)
      );
    }

    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      invoicesQuery = query(
        invoicesQuery,
        where('issueDate', '<=', endOfDay)
      );
    }

    const snapshot = await getDocs(invoicesQuery);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        issueDate: convertTimestampToDate(data.issueDate),
        dueDate: convertTimestampToDate(data.dueDate),
        paymentDate: data.paymentDate ? convertTimestampToDate(data.paymentDate) : undefined,
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : undefined
      } as Invoice;
    });
  } catch (error) {
    console.error('Error getting invoices:', error);
    throw error;
  }
};

// Get a single invoice by ID
export const getInvoiceById = async (id: string): Promise<Invoice | null> => {
  try {
    const docRef = doc(db, 'invoices', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        issueDate: convertTimestampToDate(data.issueDate),
        dueDate: convertTimestampToDate(data.dueDate),
        paymentDate: data.paymentDate ? convertTimestampToDate(data.paymentDate) : undefined,
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : undefined
      } as Invoice;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting invoice:', error);
    throw error;
  }
};

// Generate a unique invoice number
export const generateInvoiceNumber = async (): Promise<string> => {
  try {
    // Get the current year
    const currentYear = new Date().getFullYear();
    
    // Get the latest invoice to determine the next number
    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('invoiceNumber', '>=', `INV-${currentYear}-`),
      where('invoiceNumber', '<', `INV-${currentYear + 1}-`),
      orderBy('invoiceNumber', 'desc'),
    );
    
    const snapshot = await getDocs(invoicesQuery);
    
    let nextNumber = 1;
    if (!snapshot.empty) {
      const latestInvoice = snapshot.docs[0].data();
      const latestNumber = latestInvoice.invoiceNumber;
      const numberPart = parseInt(latestNumber.split('-')[2], 10);
      nextNumber = numberPart + 1;
    }
    
    // Format with leading zeros (e.g., INV-2025-0001)
    return `INV-${currentYear}-${nextNumber.toString().padStart(4, '0')}`;
  } catch (error) {
    console.error('Error generating invoice number:', error);
    throw error;
  }
};

// Create a new invoice with GL transaction
export const createInvoice = async (invoice: Omit<Invoice, 'id'>): Promise<string> => {
  try {
    // Generate invoice number if not provided
    if (!invoice.invoiceNumber) {
      invoice.invoiceNumber = await generateInvoiceNumber();
    }
    
    // Add timestamps
    const now = serverTimestamp();
    const invoiceData = {
      ...invoice,
      createdAt: now,
      updatedAt: now
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, 'invoices'), invoiceData);
    
    // Generate GL entries for this invoice
    await generateInvoiceGLEntries({
      id: docRef.id,
      invoiceNumber: invoice.invoiceNumber,
      customerName: invoice.customerName,
      issueDate: invoice.issueDate,
      totalAmount: invoice.amount,
      items: invoice.items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        glAccountId: item.glAccountId || '',
        total: item.amount
      }))
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

// Update an existing invoice and related GL transactions
export const updateInvoice = async (id: string, invoice: Partial<Invoice>): Promise<void> => {
  try {
    const invoiceRef = doc(db, 'invoices', id);
    
    // Get current invoice data
    const currentInvoiceSnap = await getDoc(invoiceRef);
    if (!currentInvoiceSnap.exists()) {
      throw new Error('Invoice not found');
    }
    
    const currentInvoice = {
      id: currentInvoiceSnap.id,
      ...currentInvoiceSnap.data()
    } as Invoice;
    
    // Update timestamps
    const updateData = {
      ...invoice,
      updatedAt: serverTimestamp()
    };
    
    // Update in Firestore
    await updateDoc(invoiceRef, updateData);
    
    // Handle GL transactions if amount or status changed
    if (invoice.amount !== undefined || 
        invoice.status !== undefined || 
        invoice.items !== undefined) {
      
      // Get existing GL transactions for this invoice
      const existingTransactions = await getGLTransactions(null, null, 'invoice', id);
      
      // Delete existing GL transactions
      for (const transaction of existingTransactions) {
        if (transaction.id) {
          await deleteGLTransaction(transaction.id);
        }
      }
      
      // Create new GL transactions based on updated invoice
      const updatedInvoice = {
        ...currentInvoice,
        ...invoice
      };
      
      await generateInvoiceGLEntries({
        id: id,
        invoiceNumber: updatedInvoice.invoiceNumber,
        customerName: updatedInvoice.customerName,
        issueDate: updatedInvoice.issueDate,
        totalAmount: updatedInvoice.amount,
        items: updatedInvoice.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          glAccountId: item.glAccountId || '',
          total: item.amount
        }))
      });
    }
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};

// Delete an invoice and its GL transactions
export const deleteInvoice = async (id: string): Promise<void> => {
  try {
    // First, check if the invoice exists
    const invoiceRef = doc(db, 'invoices', id);
    const invoiceDoc = await getDoc(invoiceRef);
    
    if (!invoiceDoc.exists()) {
      throw new Error(`Invoice with ID ${id} not found`);
    }
    
    // Get existing GL transactions for this invoice
    const existingTransactions = await getGLTransactions(null, null, 'invoice', id);
    
    // Log the transactions being deleted for debugging
    console.log(`Found ${existingTransactions.length} GL transactions for invoice ${id}`);
    
    // Delete all related GL transactions first
    for (const transaction of existingTransactions) {
      if (transaction.id) {
        console.log(`Deleting GL transaction ${transaction.id}`);
        try {
          await deleteGLTransaction(transaction.id);
        } catch (txError) {
          console.error(`Error deleting GL transaction ${transaction.id}:`, txError);
          // Continue with other transactions even if one fails
        }
      }
    }
    
    // Delete the invoice document
    console.log(`Deleting invoice document ${id}`);
    await deleteDoc(invoiceRef);
    console.log(`Successfully deleted invoice ${id}`);
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

// Subscribe to invoices with real-time updates
export const subscribeToInvoices = (
  callback: (invoices: Invoice[]) => void,
  startDate?: Date | null,
  endDate?: Date | null
) => {
  let invoicesQuery = query(
    collection(db, 'invoices'),
    orderBy('issueDate', 'desc')
  );

  // Apply date range filters if provided
  if (startDate) {
    invoicesQuery = query(
      invoicesQuery,
      where('issueDate', '>=', startDate)
    );
  }

  if (endDate) {
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);
    invoicesQuery = query(
      invoicesQuery,
      where('issueDate', '<=', endOfDay)
    );
  }

  // Set up real-time listener
  return onSnapshot(invoicesQuery, (snapshot) => {
    const invoices = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        issueDate: convertTimestampToDate(data.issueDate),
        dueDate: convertTimestampToDate(data.dueDate),
        paymentDate: data.paymentDate ? convertTimestampToDate(data.paymentDate) : undefined,
        createdAt: data.createdAt ? convertTimestampToDate(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? convertTimestampToDate(data.updatedAt) : undefined
      } as Invoice;
    });
    
    callback(invoices);
  });
};

// Get invoices by status
export const getInvoicesByStatus = async (
  startDate?: Date, 
  endDate?: Date
): Promise<{status: string, amount: number}[]> => {
  try {
    const invoices = await getInvoices(startDate, endDate);
    
    // Group invoices by status and sum amounts
    const statusMap = new Map<string, number>();
    
    invoices.forEach(invoice => {
      const status = invoice.status;
      const currentAmount = statusMap.get(status) || 0;
      statusMap.set(status, currentAmount + invoice.amount);
    });
    
    // Convert map to array
    return Array.from(statusMap.entries()).map(([status, amount]) => ({
      status,
      amount
    }));
  } catch (error) {
    console.error('Error getting invoices by status:', error);
    throw error;
  }
};

// Get invoices by customer
export const getInvoicesByCustomer = async (
  startDate?: Date, 
  endDate?: Date
): Promise<{customer: string, amount: number}[]> => {
  try {
    const invoices = await getInvoices(startDate, endDate);
    
    // Group invoices by customer and sum amounts
    const customerMap = new Map<string, number>();
    
    invoices.forEach(invoice => {
      const customer = invoice.customerName;
      const currentAmount = customerMap.get(customer) || 0;
      customerMap.set(customer, currentAmount + invoice.amount);
    });
    
    // Convert map to array
    return Array.from(customerMap.entries()).map(([customer, amount]) => ({
      customer,
      amount
    }));
  } catch (error) {
    console.error('Error getting invoices by customer:', error);
    throw error;
  }
};
